import type { AssignmentStatus } from '@/db/schema/enums'
import { formatHoursDuration, hoursToMinutes } from './duration'
import type { CurriculumItemType } from './filter-engine'
import type { ContainerRefResult, SourceOption } from './source-option'

/**
 * What finishes an assignment, mirroring `completionModeEnum` in the schema.
 *
 * A closed union rather than `string` because the only consumer branches on
 * equality with `'manual'` to decide whether marking a card done is offered at
 * all: as an open string, a typo or a renamed enum member type-checks and
 * silently removes the coach's only way to complete a manual card.
 */
export type CompletionMode = 'manual' | 'submission_review'

/**
 * Where a container's fanned-out cards are headed (LG-907).
 *
 * Named rather than restated inline at each of the six sites that pass it, so
 * a third destination — the picked-day form `StageDestination` already models
 * is the obvious next one — is a compile error at every one of them rather
 * than a silent inheritance of whichever arm happened to be last. That
 * inheritance is not hypothetical here: it is how `'picked-day-gone'` came to
 * claim `'auto'`'s sentence in the sibling flash.
 */
export type CascadeDestination = 'tray' | 'auto'

/**
 * Narrows the DTO's `string` at the one place a card is built, so nothing
 * downstream has to re-decide what an unrecognised mode means. Undefined
 * signals "not known", which is a different claim from either mode and the
 * details modal renders it as such.
 */
export function toCompletionMode(
  value: string | null | undefined,
): CompletionMode | undefined {
  return value === 'manual' || value === 'submission_review' ? value : undefined
}

export interface TrayCard {
  id: string
  title: string
  type: CurriculumItemType
  meta: string
  est: number
  done?: boolean
  fixed?: boolean
  repeat?: string
  decay?: boolean
  aiMock?: boolean
  needsLink?: boolean
  linkedItemId?: string
  originalTitle?: string
  link?: string
  estimatedMinutes?: number | null
  estimateLow?: number | null
  estimateHigh?: number | null
  estimateOverridden?: boolean
  version?: number
  learningItemId?: string | null
  /**
   * The catalog problem behind this card. Null on a one-off card and on any
   * non-problem kind — the details modal reads it to decide whether it has a
   * problem to show history and related questions for.
   */
  problemId?: string | null
  /** The effective source's own id for the problem, e.g. LeetCode's "1". */
  externalId?: string | null
  planDayId?: string | null
  weekId?: string | null
  weekVersion?: number | null
  targetCompanyId?: string | null
  /**
   * The closed `assignmentStatusEnum` lifecycle (LG-360 expert review, B4) —
   * not a bare `string`. `canRedateCard`'s allow-list branches on this, and a
   * wide `string` type let a real status (`submitted`) fall off that
   * allow-list unnoticed and come out as the wrong "why can't I move this"
   * sentence, with no compile error to catch it. Absent on cards that are
   * not assignments (calendar overlays) and on the mock board, where it
   * means "unknown, so don't restrict".
   */
  status?: AssignmentStatus
  /**
   * The coach's explicit source pick (LG-359 B) — same meaning as the
   * `CoachingAssignment.sourceId` column, so a switcher writes it straight
   * back. Null means the card wears the problem's owning source.
   */
  sourceId?: string | null
  /** How the source in effect presents; both null when none resolved. */
  sourceDisplayName?: string | null
  sourceIcon?: string | null
  /**
   * Every source this card's problem could be assigned from (LG-362 §2),
   * owning first — what the inline switcher on the card fans out. Empty
   * (or absent, on boards that don't hydrate it) means nothing to switch.
   */
  sources?: SourceOption[]
  /**
   * What finishes this card: `manual` is the coach's to mark, anything else is
   * the platform's. The details modal reads it to decide whether marking done
   * is even offered — the service rejects a manual completion on every other
   * mode (LG-360 §6). Absent on cards that are not assignments (calendar
   * overlays) and on the mock board, where it means "unknown", not
   * "platform-tracked".
   */
  completionMode?: CompletionMode
  /** Per-assignment prose shown to the client on the card. */
  instructions?: string | null
  /** Why this card suits this client — per-assignment, not catalog-level. */
  compatibilityDescription?: string | null
  /** The coach's one note for this assignment (LG-360 §3). */
  coachNote?: string | null
  /** Catalog-level notes every client assigned this item sees (LG-360 §2). */
  universalNotes?: string | null
  /**
   * Which container's cascade assign created this card (LG-907) — its
   * breadcrumb. Null on a card the coach added directly; absent on boards that
   * do not hydrate it (the mock board, calendar overlays).
   */
  creatingParent?: ContainerRefResult | null
  /**
   * The other containers this card's catalog item belongs to, shown as
   * visible text under the breadcrumb. Never repeats `creatingParent` — the
   * breadcrumb already names it.
   */
  otherMemberships?: ContainerRefResult[]
}

export type DayKind = 'normal' | 'blocked' | 'interview' | 'avail'

export interface ScheduleDay {
  label: string
  kind: DayKind
  budget?: number
  note?: string
  cards: TrayCard[]
  id?: string
  dateKey?: string
}

export type WeekStatus = 'past' | 'published' | 'draft'

/** A Sprint the client sits out between two of the plan's weeks (LG-1073). */
export interface WeekSprintGap {
  sprintId: string
  name: string
  reason: 'skipped' | 'unfunded'
}

/**
 * Read-only Sprint context the persisted board shows beside a week: which
 * Sprint the week is bound to and what that means for edits (LG-1073). Absent
 * on the mock fixtures, which carry no Sprints, so every reader treats a
 * missing value as "nothing to say".
 */
export interface WeekSprintContext {
  name: string
  /**
   * The client is locked into this Sprint and it had not ended when the plan
   * was last derived: edits here reach a paid week. Judged against the clock
   * at derivation, so it moves with a refetch, not with the wall clock.
   */
  inFlight: boolean
  /**
   * An admin flagged this running week as asking less of the client
   * (LG-1004). A label for the coach, nothing more: the week plans and
   * publishes like any other.
   */
  reducedRequirements: boolean
  /** Sprints between the previous week's and this one, in date order. */
  gapsBefore: WeekSprintGap[]
  /**
   * The first week the client's credits do not currently reach; the schedule
   * draws its divider above it.
   */
  beyondReach: boolean
  /**
   * The week is still bound to a Sprint the client is no longer in: the
   * reconcile had no open slot to move it to and left it where it was. It
   * moves on its own once one opens.
   */
  leftBehind: 'skipped' | 'unfunded' | null
}

export interface Week {
  id: string
  label: string
  status: WeekStatus
  sprint?: WeekSprintContext
  /**
   * Presentation only: the schedule tucks folded weeks behind its "show N
   * earlier week(s)" disclosure and reveals them collapsed. Never a gate on
   * editability or placement — that is `status`'s job, and the two are
   * independent (a folded week has always elapsed, but most elapsed weeks are
   * not folded — and an elapsed *published* week still reports `published`).
   * Each producer decides for itself what deserves folding: the persisted
   * board folds elapsed draft and published weeks nobody ever put a card in,
   * plus whatever elapsed weeks the coach folded by hand (a per-coach,
   * per-engagement viewing preference — see `foldedWeek`); the mock
   * fixtures fold by age.
   */
  folded: boolean
  /**
   * Whether this week's dates are behind us. Deliberately not the same
   * question as `status === 'past'`: a published week keeps reporting
   * `published` once it elapses, because its badge and its read-only rules
   * answer to the client, not to the calendar.
   *
   * Required, so a reader never has to decide what an absent answer means:
   * the persisted board reads the engagement's real dates, and the mock
   * fixtures — which carry labels rather than dates — derive it from the
   * badge through `withMockElapsed`.
   */
  elapsed: boolean
  /**
   * Whether the client has done any work on this week's cards — the answer
   * `unpublishBlockedReason` gates on (LG-590).
   *
   * Required for the same reason `elapsed` is: an absent answer would leave
   * every reader guessing, and the guess that used to be made here — read the
   * cards and look for a `submitted`/`completed` mark — is precisely the bug.
   * The server decides this from three sources, two of which are rows the
   * plan DTO does not carry (the status-event ledger, because the marks are
   * erasable; and the submission table, because a reopened submission leaves
   * no mark at all), so it cannot be re-derived here at any fidelity.
   */
  clientActivity: boolean
  open: boolean
  days: ScheduleDay[]
  version?: number
}

export type PlaceResult =
  | { placed: true; weekIdx: number; dayIdx: number; where: string }
  | { placed: false }

export const cardHours = (card: TrayCard): number => (card.fixed ? 0 : card.est)

const usedHours = (day: ScheduleDay): number =>
  day.cards.reduce((total, card) => total + cardHours(card), 0)

// Declared beside `WeekStatus`: which members mean "elapsed" is that type's
// knowledge, and a consumer that reimplements it from memory is how a week
// that must not be edited becomes editable. This is the *status* gate — the
// one every read-only rule hangs off — and not the calendar question, which
// `elapsed` answers.
export const isPastWeek = (week: Pick<Week, 'status'>): boolean =>
  week.status === 'past'

/**
 * Why this published week cannot go back to draft, or `null` when it can.
 *
 * The server's rule surfaced for the coach so the board can say why before
 * spending a round trip on it (LG-361 §12): unpublishing is allowed only
 * while the week holds no client work, because taking a week back after the
 * client has answered it would hide their own work from them.
 *
 * The "has the client worked on this" half is now *read*, not *restated*
 * (LG-590). It used to be reconstructed here by scanning the week's cards for
 * a `done` flag or a `submitted`/`completed` status, which is a strictly
 * weaker question than the one `unpublishWeek` asks: the server also consults
 * the status-event ledger (the assignment's own marks are erasable — a coach
 * un-doing a completion clears them) and the submission table (a submission
 * the client later had reopened leaves no mark on either). Both gaps produced
 * the same failure — an enabled button, and a refusal the coach could not act
 * on, delivered as the generic `PRECONDITION_FAILED` sentence because
 * `SERVICE_ERROR_MESSAGES` is code-grained rather than cause-grained.
 *
 * So `clientActivity` is projected per week through `curriculum-query.ts`,
 * where the rows that answer it actually live. The elapsed check below stays
 * local: it is a question about dates, which the board already knows.
 */
export function unpublishBlockedReason(week: Week): string | null {
  // `assertWeekNotPast` refuses an elapsed week, and rightly: taking one back
  // to draft would strip the client's record of a week they have lived
  // through without buying the coach an edit, since every editing command
  // refuses it too.
  if (week.elapsed) {
    return 'This week has already elapsed — it can no longer be edited, so unpublishing it would only hide it from the client.'
  }
  return week.clientActivity
    ? 'The client has already worked on this week — it can no longer be unpublished.'
    : null
}

export function autoPlaceInto(weeks: Week[], item: TrayCard): PlaceResult {
  for (const [weekIdx, week] of weeks.entries()) {
    if (isPastWeek(week)) continue

    for (const [dayIdx, day] of week.days.entries()) {
      if (day.kind !== 'normal') continue
      // Minutes, not hours — see `hoursToMinutes`.
      if (
        hoursToMinutes(usedHours(day) + cardHours(item)) >
        hoursToMinutes(day.budget ?? 0)
      )
        continue

      const weekName = week.label.split(' · ')[0]
      return {
        placed: true,
        weekIdx,
        dayIdx,
        where: `"${item.title}" scheduled on ${day.label} (${weekName}) — first day with ${formatHoursDuration(cardHours(item))} free`,
      }
    }
  }
  return { placed: false }
}

const appendCard = (
  weeks: Week[],
  weekIdx: number,
  dayIdx: number,
  item: TrayCard,
): Week[] =>
  weeks.map((week, currentWeekIdx) => {
    if (currentWeekIdx !== weekIdx) return week
    return {
      ...week,
      days: week.days.map((day, currentDayIdx) =>
        currentDayIdx === dayIdx
          ? { ...day, cards: [...day.cards, item] }
          : day,
      ),
    }
  })

export function distribute(
  weeks: Week[],
  tray: TrayCard[],
): { weeks: Week[]; leftovers: TrayCard[]; summary: string } {
  let distributedWeeks = weeks
  const leftovers: TrayCard[] = []
  let placedCount = 0

  for (const item of tray) {
    const result = autoPlaceInto(distributedWeeks, item)
    if (!result.placed) {
      leftovers.push(item)
      continue
    }

    distributedWeeks = appendCard(
      distributedWeeks,
      result.weekIdx,
      result.dayIdx,
      item,
    )
    placedCount += 1
  }

  const leftoverCopy = leftovers.length
    ? ` — ${leftovers.length} didn't fit and stayed in the tray.`
    : '.'
  return {
    weeks: distributedWeeks,
    leftovers,
    summary: `✦ Distributed ${placedCount} card(s) by daily hours${leftoverCopy}`,
  }
}

const sameCards = (left: TrayCard[], right: TrayCard[]): boolean =>
  left.length === right.length &&
  left.every((card, index) => card === right[index])

export function optimize(weeks: Week[]): { weeks: Week[]; summary: string } {
  const plannedCards = weeks.map((week) =>
    week.days.map((day) =>
      week.status === 'draft' && day.kind === 'normal'
        ? day.cards.filter((card) => card.fixed || card.done)
        : day.cards,
    ),
  )
  const pool: {
    card: TrayCard
    weekIdx: number
    dayIdx: number
    order: number
    priority: boolean
  }[] = []

  for (const [weekIdx, week] of weeks.entries()) {
    if (week.status !== 'draft') continue
    for (const [dayIdx, day] of week.days.entries()) {
      if (day.kind !== 'normal') continue
      for (const card of day.cards) {
        if (card.fixed || card.done) continue
        pool.push({
          card,
          weekIdx,
          dayIdx,
          order: pool.length,
          priority: Boolean(card.decay || card.repeat !== undefined),
        })
      }
    }
  }

  pool.sort(
    (left, right) =>
      Number(right.priority) - Number(left.priority) ||
      left.order - right.order,
  )

  const leftovers: typeof pool = []
  for (const entry of pool) {
    let placed = false
    for (const [weekIdx, week] of weeks.entries()) {
      if (week.status !== 'draft') continue
      for (const [dayIdx, day] of week.days.entries()) {
        if (day.kind !== 'normal') continue
        const cards = plannedCards[weekIdx][dayIdx]
        const used = cards.reduce((total, card) => total + cardHours(card), 0)
        if (
          hoursToMinutes(used + cardHours(entry.card)) >
          hoursToMinutes(day.budget ?? 0)
        )
          continue
        plannedCards[weekIdx][dayIdx] = [...cards, entry.card]
        placed = true
        break
      }
      if (placed) break
    }
    if (!placed) leftovers.push(entry)
  }

  for (const entry of leftovers) {
    plannedCards[entry.weekIdx][entry.dayIdx] = [
      ...plannedCards[entry.weekIdx][entry.dayIdx],
      entry.card,
    ]
  }

  let anyWeekChanged = false
  const optimizedWeeks = weeks.map((week, weekIdx) => {
    let anyDayChanged = false
    const days = week.days.map((day, dayIdx) => {
      const cards = plannedCards[weekIdx][dayIdx]
      if (sameCards(day.cards, cards)) return day
      anyDayChanged = true
      return { ...day, cards }
    })

    if (!anyDayChanged) return week
    anyWeekChanged = true
    return { ...week, days }
  })

  // The summary must reflect what actually happened: leftovers returned to
  // their original day can push it over budget, and a no-op pass moved nothing.
  if (!anyWeekChanged) {
    return {
      weeks,
      summary: '✦ Draft weeks already optimized — nothing to move.',
    }
  }
  return {
    weeks: optimizedWeeks,
    summary:
      leftovers.length > 0
        ? `✦ Optimized draft weeks: decay/repeat first. ${leftovers.length} card(s) stayed on their original day — no room within hour budgets.`
        : "✦ Optimized draft weeks: decay/repeat first, packed within each day's hour budget.",
  }
}
